/**
 * ☁️ Who wins when two devices disagree about a run (v3.60).
 *
 * Kept in utils, apart from useRunSync, for two reasons: it is the only
 * part of the sync with real teeth — it decides whose play survives — so
 * it deserves direct tests; and importing the composable pulls the whole
 * Firestore SDK (grpc and all) into a plain node test bundle.
 *
 * `rev` counts checkpoints this device has written. `baseRev` is the rev
 * it last agreed with the cloud on — set both when it pushes and when it
 * adopts. So "moved" simply means rev > baseRev on that side.
 */
export type SyncVerdict = 'seed' | 'push' | 'adopt' | 'ask' | 'noop'

export function resolveSync(state: {
  localRev: number
  baseRev: number
  remoteRev: number
  hasLocalRun: boolean
  remoteExists: boolean
}): SyncVerdict {
  const { localRev, baseRev, remoteRev, hasLocalRun, remoteExists } = state
  // Nothing in the cloud: this device's run becomes the first copy.
  if (!remoteExists) return hasLocalRun ? 'seed' : 'noop'
  const localMoved = localRev > baseRev
  const remoteMoved = remoteRev > baseRev
  if (!remoteMoved) return localMoved ? 'push' : 'noop'
  // The headline case — started on the phone, opened the PC: this device
  // has played nothing since it last agreed, so it just takes the run.
  if (!localMoved) return 'adopt'
  // Both moved on. Picking silently is how an hour of play disappears.
  return 'ask'
}

/**
 * 🎲 What this device's local save means for the CLOUD doc (v6.33).
 *
 * Weekly expeditions carry `mode: 'daily'` and are device-local by
 * design. The trap is that clearing a run destroys the very field the
 * guard reads: abandoning a weekly leaves `run.value === null`, whose
 * `?.mode` is undefined, which used to read as "the campaign ended" and
 * DELETED the campaign's cloud save. So the caller passes the mode of
 * the slot that was just cleared, and it is honoured over the (absent)
 * live one.
 */
export function cloudAction(state: {
  hasRun: boolean
  /** Mode of the LIVE run, if there is one. */
  mode?: 'daily' | 'tutorial'
  /** Mode of the run this device last held — survives it being cleared. */
  lastActiveMode?: 'daily' | 'tutorial'
}): 'write' | 'delete' | 'skip' {
  // 🎓 v7.04: the First Dispatch never touches the cloud either.
  if (state.mode === 'daily' || state.mode === 'tutorial') return 'skip'
  if (state.hasRun) return 'write'
  // No run in hand: only the CAMPAIGN ending may drop the cloud copy.
  // 🎓 v7.40 (per Javi, from the parked onboarding WIP): nor may a PRACTICE
  // ending - it lived in the campaign's local slot, so leaving it empties
  // that slot, and a flush then read the empty slot as the campaign ending
  // and tombstoned the campaign save parked in the cloud.
  return state.lastActiveMode === 'daily' || state.lastActiveMode === 'tutorial' ? 'skip' : 'delete'
}

/** Whether a local checkpoint should bump the campaign's rev. A weekly's
 * churn — including the checkpoint that CLEARS it — must not, or a later
 * flush() pushes (or deletes) the campaign on its behalf. */
export function countsAsProgress(state: { mode?: 'daily' | 'tutorial'; clearedMode?: 'daily' | 'tutorial' }): boolean {
  // 🎓 v7.04: a First Dispatch checkpoint never bumps the campaign rev either.
  const lane = state.mode ?? state.clearedMode
  return lane !== 'daily' && lane !== 'tutorial'
}
